"use client";

import { usePresence } from "motion/react";
import React from "react";
import Animate from "@/components/animations/Animate";
import Flex from "@/components/layout/Flex";
import Button from "@/components/ui/Button";
import { HREF_EMAIL, HREF_PHONE_NUMBER, URLS } from "@/lib/consts";
import { breakpointClasses, cn, splitBreakpointProps } from "@/lib/utils";
import type { FloatingContactButtonProps } from "./types";

function FloatingContactButton({
  className,
  ...props
}: FloatingContactButtonProps) {
  const [showOptions, setShowOptions] = React.useState(false);
  const [isPresent, safeToRemove] = usePresence();
  const { bp, native } = splitBreakpointProps(props);
  const classes = breakpointClasses(bp);

  const handleOnMouseEnter = React.useCallback(() => {
    setShowOptions(true);
  }, []);

  const handleOnMouseLeave = React.useCallback(() => {
    setShowOptions(false);
  }, []);

  const handleOnClick = React.useCallback(() => {
    setShowOptions(value => !value);
  }, []);

  React.useEffect(() => {
    if (!isPresent) {
      window.setTimeout(safeToRemove, 1000);
    }
  }, [isPresent, safeToRemove]);

  return (
    <Animate
      as={Flex.Column}
      layout={true}
      transition={{ layout: { duration: 0.3 } }}
      justify="center"
      items="center"
      className={cn(
        "hidden md:flex",
        "fixed bottom-10 right-10 z-50 rounded-sm bg-popover",
        className,
        classes,
      )}
      onMouseEnter={handleOnMouseEnter}
      onMouseLeave={handleOnMouseLeave}
      {...native}
    >
      <Animate.Presence>
        {showOptions && (
          <Flex.Column items="end">
            <Button.Icon
              variant="ghost"
              icon="message-square"
              href={URLS.CONTACT_US}
              aria-label="Goto contact us page"
            />

            <Button.Icon
              variant="ghost"
              icon="mail"
              href={HREF_EMAIL}
              aria-label="Email us"
            />

            <Button.Icon
              variant="ghost"
              icon="phone"
              href={HREF_PHONE_NUMBER}
              aria-label="Call us"
            />
          </Flex.Column>
        )}
      </Animate.Presence>

      <Button.Icon
        icon="phone-call"
        variant="default"
        onClick={handleOnClick}
        aria-label="Open quick contact us menu"
      />
    </Animate>
  );
}

FloatingContactButton.displayName = "FloatingContactButton";

export default FloatingContactButton;
